import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  View,
  type ListRenderItemInfo,
  FlatList,
  Dimensions,
} from 'react-native';

import MoneyBillSVG from '~/assets/svgs/money-bill.svg';
import CheckBalloonSVG from '~/assets/svgs/check-balloon.svg';
import TagSVG from '~/assets/svgs/tag.svg';
import PlusSVG from '~/assets/svgs/plus.svg';

import DefaultScreenContainer from '~/components/DefaultScreenContainer';

import BannerImg from '~/assets/img/banners/marisa-takes-care-banner.png';
import BannerImgDesk from '~/assets/img/banners/marisa-takes-care-banner-desk-offer.png';
import MarisaTakesCareSvg from '~/assets/svgs/marisa-takes-care-logo.svg';
import {
  Button,
  ContinueToContractButton,
  InsuranceCardBenefit,
  OfferBanner,
  Text,
} from '~/components';
import ExitConfirmationModal from '../components/ExitConfirmationModal';
import { GENERAL_CONDITIONS_LINK } from '../constants';
import { useUserInfoStore } from '~/stores/userInfo';
import { journeyLogUseCase } from '~/useCases/PSF/JourneyLogUseCase';
import { callNumber, isWeb } from '~/utils/device';
import type { Benefit } from '~/components/InsuranceCardBenefit';
import { MarisaAppUtils } from '~/utils/MarisaAppUtils';
import DelboniSVG from '~/assets/svgs/delboni-auriemo.svg';
import FemmeSVG from '~/assets/svgs/femme.svg';
import LavoisierSVG from '~/assets/svgs/lavoisier.svg';
import FleurySVG from '~/assets/svgs/fleury.svg';
import APlusSVG from '~/assets/svgs/a-plus-medicina-diagnostica.svg';
import SabinSVG from '~/assets/svgs/sabin-medicina-diagnostica.svg';
import SalomaoZoppiSVG from '~/assets/svgs/salomao-zoppi.svg';
import type { SvgProps } from 'react-native-svg';
import { useSalesFunnel } from '~/stores';
import Table, { type ColumnProps } from '~/components/Table';
import { CAPITALS_CONTACT_PHONE, CONTACT_PHONE } from '~/utils/services';

const INSURANCE_TYPE = 'marisa-cuida';
const CONTRACT_SCREEN = 'MarisaServicos_MarisaTakesCareContract';

const SCREEN_WIDTH =
  Dimensions.get('window').width > 1024 ? 1024 : Dimensions.get('window').width;
const PARTNER_ITEM_WIDTH = 135;

const BENEFITS: Benefit[] = [
  {
    icon: CheckBalloonSVG,
    title: 'Telemedicina gratuita e ilimitada',
    detailsList: [
      'Para titular e dependentes',
      'Profissionais generalistas ou especialistas',
      'Disponível 24 horas e 7 dias por semana',
    ],
  },
  {
    icon: TagSVG,
    title: 'Economia',
    detailsList: [
      <Text>
        Até <Text className="text-primary-600">80% de desconto</Text> em
        consultas e exames
      </Text>,
      <Text>
        Até <Text className="text-primary-600">80% de desconto</Text> em
        medicamentos em mais de 31.000 farmácias pelo Brasil
      </Text>,
      <Text>
        <Text className="text-primary-600">Subsídio de R$100,00</Text> em
        medicamentos para o titular 3x ao ano
      </Text>,
      <Text>
        <Text className="text-primary-600">Direito a check up </Text> 1x ao ano
        para o titular, carência de 180 dias para realizar o procedimento{' '}
        {'(Cobertura somente para o plano de '}
        <Text className="font-bold">29,90 </Text>
        {'por mês) '}
      </Text>,
    ],
  },
  {
    icon: MoneyBillSVG,
    title: 'Vantagens',
    detailsList: [
      'Até 3 dependentes (não precisa ser familiar e sem limite de idade)',
      'Não tem carência, em até 48 hrs já pode utilizar',
      'Pagamentos de consultas e exames direto com o profissional, somente quando utilizar',
    ],
  },
];

const COLUMNS: ColumnProps[] = [
  {
    id: 1,
    texts: [
      'Procedimento',
      'Consulta cardiologista',
      'Consulta Clinico Geral',
      'Ecocardiograma',
      'Eletrocardiograma',
      'Hemograma',
      'Teste Ergométrico',
      'Total Geral',
    ],
  },
  {
    id: 2,
    texts: [
      'Particular',
      'R$ 150,00',
      'R$ 150,00',
      'R$ 90,00',
      'R$ 90,00',
      'R$ 28,00',
      'R$ 259,00',
      'R$ 767,00',
    ],
  },
  {
    id: 3,
    texts: [
      'Rede mais',
      'R$ 70,00',
      'R$ 70,00',
      'R$ 45,00',
      'R$ 45,00',
      'R$ 12,00',
      'R$ 54,52',
      'R$ 296,52',
    ],
  },
  {
    id: 4,
    texts: [
      'Economia',
      'R$ 80,00',
      'R$ 80,00',
      'R$ 45,00',
      'R$ 45,00',
      'R$ 16,00',
      'R$ 204,48',
      'R$ 470,48',
    ],
    variant: 'secondary',
  },
  {
    id: 5,
    texts: ['% Economia', '53%', '53%', '50%', '50%', '57%', '79%', '61%'],
    variant: 'secondary',
  },
];

const partners = [
  DelboniSVG,
  FemmeSVG,
  LavoisierSVG,
  FleurySVG,
  APlusSVG,
  SabinSVG,
  SalomaoZoppiSVG,
];

const Details: React.FC = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [partnersData, setPartnersData] = useState<React.FC<SvgProps>[]>([
    ...partners,
  ]);

  const { init } = useSalesFunnel();

  const id = useUserInfoStore((state) => state.user?.id);

  const partnersList = useRef<FlatList>(null);
  const interval = useRef<NodeJS.Timeout>();
  const lastIndex = useRef<number>(0);

  const renderPartner = useCallback(
    ({ item: Icon }: ListRenderItemInfo<React.FC<SvgProps>>) => (
      <View className="mx-2">
        <Icon />
      </View>
    ),
    []
  );

  const handleAddMore = useCallback(() => {
    setPartnersData((prev) => [...prev, ...partners]);
  }, []);

  useEffect(() => {
    init('marisa-cuida');
    if (id) {
      journeyLogUseCase(useUserInfoStore.getState().user, 'MARISACUIDA');
    }
  }, [id, init]);

  useEffect(() => {
    if (interval.current) {
      clearInterval(interval.current);
    }

    interval.current = setInterval(() => {
      const index = Math.floor((SCREEN_WIDTH - 24 * 2) / PARTNER_ITEM_WIDTH);

      if (lastIndex.current === 0) {
        lastIndex.current = index;
      } else {
        lastIndex.current += index;
      }

      if (lastIndex.current < partnersData.length - 1) {
        partnersList.current?.scrollToIndex({
          index: lastIndex.current,
          animated: true,
        });
      }
    }, 2000);
  }, [partnersData]);

  return (
    <DefaultScreenContainer>
      <ExitConfirmationModal
        visible={modalVisible}
        onDismiss={() => setModalVisible(false)}
      />

      <OfferBanner
        hireInsurance={
          MarisaAppUtils.disableInsurance
            ? {
                showButton: true,
                type: INSURANCE_TYPE,
                label: 'Contrate em nossas lojas',
                onPressButton: MarisaAppUtils.goToStoresList,
              }
            : {
                showButton: true,
                type: INSURANCE_TYPE,
                redirectScreen: CONTRACT_SCREEN,
              }
        }
        images={{
          mobile: BannerImg,
          web: { banner: BannerImgDesk, badge: MarisaTakesCareSvg },
        }}
        insuranceName="Marisa Cuida"
        insuranceDetail="A Marisa Cuida da sua saúde
          para você"
      />

      <View className="px-6">
        <View className="md:items-center md:justify-center items-start justify-start">
          <Text className="mt-4 text-gray-700 text-[22px] font-medium">
            O que é o Marisa Cuida?
          </Text>

          <Text className="mt-4 text-gray-400 md:text-center text-[16px] leading-6">
            O Marisa Cuida é um programa de saúde em parceria com Rede Mais, uma
            plataforma que torna a medicina mais acessível e humanizada.
          </Text>
        </View>

        <View className="p-[10px] bg-health-200 rounded-md mt-8">
          <Text className="text-base text-center">
            Tenha acesso a mais de{' '}
            <Text className="font-medium">11 mil prestadores</Text> em mais de
            <Text className="font-medium"> 1.200 municípios</Text>
          </Text>
        </View>

        {isWeb && (
          <View className="mt-10 mb-2 bg-gray-100 h-[1px] invisible md:visible" />
        )}

        <Text className="mt-8 mb-3 text-xl text-gray-700 md:items-center md:justify-center md:text-center md:mb-6">
          Conheça os benefícios:
        </Text>

        <View className="bg-health-300 p-3 rounded-[16px] mb-3">
          <View className="bg-light-100 p-2 rounded-lg border border-primary-600 items-center justify-center">
            <Text className="font-medium text-base text-primary-600">
              {/* R${' '} */}
              <Text className="text-[22px] font-medium">{`R$ 13,99 ou 29,90 por mês`}</Text>
            </Text>
            <View className="flex-row items-center">
              <PlusSVG width={20} height={20} />
              <Text className="text-base text-primary-600 ml-2">
                3 dependentes sem custo
              </Text>
            </View>
          </View>
        </View>

        <View className="lg:flex-row">
          {BENEFITS.map((benefit, index) => (
            <InsuranceCardBenefit data={benefit} type="health" key={index} />
          ))}
        </View>

        <Text className="text-center mt-10 mb-6 font-medium text-lg">
          Exemplos de economia
        </Text>

        <Table columns={COLUMNS} />

        <Text className="text-center mt-10 mb-5 font-medium text-lg">
          Alguns dos nossos laboratórios
        </Text>

        <FlatList
          horizontal
          ref={partnersList}
          data={partnersData}
          scrollEnabled={false}
          decelerationRate={'normal'}
          renderItem={renderPartner}
          keyExtractor={(_, index) => String(index)}
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ alignItems: 'center' }}
          onEndReached={handleAddMore}
          onEndReachedThreshold={0.5}
        />

        <View className="items-center justify-center py-8 bg-feedback-neutral-bkg rounded-lg mb-7 mt-8">
          <Text className="text-secondary-800 text-sm">
            Tudo isso por R$ <Text className="text-xl font-medium">29,90</Text>
            /mês
          </Text>
          <Text className="text-secondary-800 text-sm">
            No cartão de crédito de sua preferência
          </Text>
        </View>
      </View>
      <View className="py-4 px-6 items-center justify-center lg:mx-auto lg:w-[328px]">
        {MarisaAppUtils.disableInsurance ? (
          <Button onPress={MarisaAppUtils.goToStoresList}>
            Contrate em nossas lojas
          </Button>
        ) : (
          <ContinueToContractButton
            type={INSURANCE_TYPE}
            navigateTo={CONTRACT_SCREEN}
            buttonLabel={isWeb ? 'Contrate agora' : 'Continuar'}
          />
        )}
      </View>
      <View className="mt-16 mb-10 gap-1">
        <Text className="text-xs text-center text-gray-400 ">
          Condições gerais do Marisa Cuida:{' '}
          <Text
            className="text-primary-600 font-semibold"
            onPress={() => MarisaAppUtils.openUrl(GENERAL_CONDITIONS_LINK)}
          >
            Consulte aqui
          </Text>
        </Text>
        <Text className="text-xs text-center text-gray-400">
          Atendimento ao cliente 24 horas, 7 dias por semana
        </Text>
        <Text className="text-xs text-center text-gray-400 ">
          Capitais e Regiões Metropolitanas:{' '}
          <Text
            className="text-primary-600 font-semibold"
            onPress={() => callNumber(CAPITALS_CONTACT_PHONE)}
          >
            {CAPITALS_CONTACT_PHONE}
          </Text>
        </Text>
        <Text className="text-xs text-center text-gray-400 ">
          Serviço de atendimento SAC:{' '}
          <Text
            className="text-primary-600 font-semibold"
            onPress={() => callNumber(CONTACT_PHONE)}
          >
            {CONTACT_PHONE}
          </Text>
        </Text>
      </View>
    </DefaultScreenContainer>
  );
};

export default Details;
